
// src/components/features/scanner/ScanCounter.tsx

import { useState, useEffect, useRef } from "react";
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Scan, LogIn, CreditCard } from 'lucide-react';
import { useScanQuota } from '@/hooks/useScanQuota';
import { useScanState } from '@/hooks/ScanStateContext';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { scanQuotaService } from '@/services/scanQuotaService';

interface ScanCounterProps {
  className?: string;
  onSubscribeClick?: () => void;
}

export function ScanCounter({ className, onSubscribeClick }: ScanCounterProps) {
  const { quota, isLoading, refreshQuota } = useScanQuota();
  const { state } = useScanState();
  const status = state.status;
  const [remaining, setRemaining] = useState<number | null>(null);
  const lastCaptureRef = useRef(false);

  // On mount, bust cache and refresh quota
  useEffect(() => {
    scanQuotaService.invalidateCache();
    refreshQuota();
  }, [refreshQuota]);

  // Sync local remaining with authoritative quota
  useEffect(() => {
    if (quota && typeof quota.remainingScans === 'number') {
      setRemaining(quota.remainingScans);
    }
  }, [quota?.remainingScans]);

  // Track status transitions for optimistic decrement and refresh
  useEffect(() => {
    if (status === 'capturing' && !lastCaptureRef.current) {
      setRemaining((prev) => (typeof prev === 'number' && prev > 0 ? prev - 1 : prev));
      lastCaptureRef.current = true;
    } else if (status !== 'capturing') {
      lastCaptureRef.current = false;
    }

    if (status === 'success' || status === 'error') {
      scanQuotaService.invalidateCache();
      refreshQuota();
    }
  }, [status, refreshQuota]);

  // don't render until we've got a number
  if (isLoading || !quota || remaining === null) {
    return null;
  }

  const getScanCountText = () => {
    if (quota.isGuest) {
      return `${remaining} trial ${remaining === 1 ? "scan" : "scans"} remaining`;
    }
    if (quota.isSubscribed) {
      if (quota.subscriptionTier === 'pro') {
        // FIXED: Display "Unlimited scans" for Pro users
        return "Unlimited scans (Pro)";
      }
      if (quota.subscriptionTier === 'basic') {
        return `${remaining} of 30 scans remaining (Basic)`;
      }
      return `${remaining} scans remaining (Subscribed)`;
    }
    return `${remaining} ${remaining === 1 ? "scan" : "scans"} remaining`;
  };

  const getVariant = () => {
    if (quota.isSubscribed && quota.subscriptionTier === 'pro') {
      return { color: "bg-green-50 border-green-200", textColor: "text-green-700", icon: "text-green-600", showCTA: false };
    }
    if (remaining <= 0) {
      return {
        color: "bg-red-50 border-red-200", textColor: "text-red-700", icon: "text-red-600",
        showCTA: true,
        ctaText: quota.isGuest ? "Sign up for 2 free scans" : "Subscribe for more scans",
        ctaLink: "/profile#subscription",
        ctaIcon: quota.isGuest ? LogIn : CreditCard
      };
    }
    if (quota.isGuest) {
      const ctaText = remaining <= 1 ? "Sign up for 2 free scans" : "Sign up for more free scans";
      return {
        color: "bg-amber-50 border-amber-200", textColor: "text-amber-700", icon: "text-amber-600",
        showCTA: true,
        ctaText,
        ctaLink: "/profile",
        ctaIcon: LogIn
      };
    }
    if (remaining <= 2) {
      return {
        color: "bg-amber-50 border-amber-200", textColor: "text-amber-700", icon: "text-amber-600",
        showCTA: true,
        ctaText: "Subscribe for more",
        ctaLink: "/profile#subscription",
        ctaIcon: CreditCard
      };
    }
    return { color: "bg-blue-50 border-blue-200", textColor: "text-blue-700", icon: "text-blue-600", showCTA: false };
  };

  const variant = getVariant();
  const handleCTAClick = variant.showCTA && onSubscribeClick ? onSubscribeClick : undefined;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`${className || ''}`}>
      <Alert className={`${variant.color} w-full sm:w-fit`}>
        <Scan className={`h-4 w-4 ${variant.icon} mr-2`} />
        <AlertDescription className={`${variant.textColor} flex items-center justify-between w-full gap-4`}>
          <span>{getScanCountText()}</span>
          {variant.showCTA && (
            <Button
              size="sm"
              variant="outline"
              className={`ml-auto whitespace-nowrap border-current hover:bg-opacity-10 hover:bg-current ${variant.textColor}`}
              onClick={handleCTAClick}
              asChild={!handleCTAClick}
            >
              {handleCTAClick ? (
                <>
                  {variant.ctaIcon && <variant.ctaIcon className="h-4 w-4 mr-1" />}
                  {variant.ctaText}
                </>
              ) : (
                <Link to={variant.ctaLink}>
                  {variant.ctaIcon && <variant.ctaIcon className="h-4 w-4 mr-1" />}
                  {variant.ctaText}
                </Link>
              )}
            </Button>
          )}
        </AlertDescription>
      </Alert>
    </motion.div>
  );
}

export default ScanCounter;
